import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, Table, Input, Select, Space, Tag, Avatar, Tooltip, App, Button, Modal, Radio, Flex } from 'antd';
import { DeleteOutlined, EditOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { get, patch, del } from '../../api/client';
import { useAuthStore } from '../../stores/authStore';

interface AdminUserRow {
  id: string;
  username: string;
  fullName?: string;
  phone?: string;
  email?: string;
  avatar?: string;
  /** Party.status 值域：激活 / 停用 / 禁用 */
  status?: string;
  /** 停用/禁用原因，恢复为「激活」时后端会清空 */
  stopReason?: string;
  role?: string;
  /** 后端算好的管理员标记；库里的历史值「管理员」不等于 'admin'，故只认这个字段 */
  isAdmin?: boolean;
  isUser?: boolean;
  createdAt?: string;
  lastLogin?: string;
}

/** GET /admin/users 分页结果 */
interface UsersResponse {
  items: AdminUserRow[];
  total: number;
  page: number;
  pageSize: number;
}

/** DELETE /admin/users/:id 回传的注销结果（与本人注销同一套判据） */
interface CancellationResult {
  mode: 'erase' | 'deactivate';
  reasons: string[];
  stats: {
    orphanOrders: number;
    interactiveOrders: number;
    orphanCustomers: number;
    keptCustomers: number;
    voucherFiles: number;
  };
  phone: string;
}

const PAGE_SIZE = 20;

const USER_STATUSES = ['激活', '停用', '禁用'] as const;
type UserStatus = (typeof USER_STATUSES)[number];

const statusTag: Record<string, { color: string; label: string }> = {
  激活: { color: 'green', label: '激活' },
  停用: { color: 'default', label: '停用' },
  禁用: { color: 'red', label: '禁用' },
};

/** 三种状态在后台的含义，直接写进弹窗，避免管理员点了才知道后果。 */
const STATUS_HELP: Record<UserStatus, string> = {
  激活: '正常使用，可登录、可下单。',
  停用: '仍可登录与修改资料，但不能提交新订单；无需填写原因。',
  禁用: '拒绝登录，界面提示「账号已被管理员禁用，如需恢复请提交申诉」；必须填写原因。',
};

function displayName(row: AdminUserRow) {
  return row.fullName || row.username || row.email || row.phone || '未命名账号';
}

export default function UsersPage() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const currentAdminId = useAuthStore((s) => s.user?.id);
  const [page, setPage] = useState(1);
  const [keyword, setKeyword] = useState('');
  const [type, setType] = useState('');
  const [statusTarget, setStatusTarget] = useState<AdminUserRow | null>(null);
  const [statusValue, setStatusValue] = useState<UserStatus>('激活');
  const [stopReason, setStopReason] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<AdminUserRow | null>(null);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['admin-users', page, keyword, type],
    queryFn: () =>
      get<UsersResponse>('/admin/users', {
        page,
        pageSize: PAGE_SIZE,
        keyword,
        ...(type ? { type } : {}),
      }),
    meta: { errorMessage: '加载用户列表失败' },
  });

  const statusMutation = useMutation({
    // 后端 DTO 收的是 snake_case（请求体在边界统一转下划线），这里不能写 stopReason
    mutationFn: (v: { id: string; status: UserStatus; reason: string }) =>
      patch<{ id: string; status: string }>(`/admin/users/${v.id}/status`, {
        status: v.status,
        stop_reason: v.reason || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setStatusTarget(null);
      message.success('状态已更新');
    },
    onError: (error: Error) => {
      message.error(`状态更新失败：${error.message}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => del<CancellationResult>(`/admin/users/${id}`),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      queryClient.invalidateQueries({ queryKey: ['admin-customers'] });
      setDeleteTarget(null);
      const stats = result?.stats;
      const detail = stats
        ? `已删除 ${stats.orphanOrders} 笔孤立订单、${stats.orphanCustomers} 个孤立客户档案，回收 ${stats.voucherFiles} 张凭证图片。`
        : '';
      if (result?.mode === 'erase') {
        message.success(`账号已注销，资料已整行删除。${detail}`);
      } else {
        message.warning(`账号已注销（登录凭据已清除），因存在往来数据，客户档案已保留。${detail}`);
      }
    },
    onError: (error: Error) => {
      message.error(`注销失败：${error.message}`);
    },
  });

  const openStatusModal = (row: AdminUserRow) => {
    setStatusTarget(row);
    setStatusValue((row.status as UserStatus) ?? '激活');
    setStopReason(row.stopReason ?? '');
  };

  const submitStatus = () => {
    if (!statusTarget) return;
    if (statusValue === '禁用' && !stopReason.trim()) {
      // 后端同一条规则，放在这里是为了不让人点提交才看到红字
      message.warning('禁用账号必须填写原因');
      return;
    }
    statusMutation.mutate({ id: statusTarget.id, status: statusValue, reason: stopReason.trim() });
  };

  const columns = [
    {
      title: '用户',
      key: 'name',
      render: (_: unknown, r: AdminUserRow) => (
        <Space>
          <Avatar src={r.avatar || undefined}>{displayName(r).slice(0, 1)}</Avatar>
          <div>
            <div>{displayName(r)}</div>
            <div style={{ fontSize: 12, color: '#999' }}>@{r.username || '（已注销，用户名已释放）'}</div>
          </div>
        </Space>
      ),
    },
    { title: '手机号', dataIndex: 'phone', width: 130, render: (v: string) => v || '-' },
    { title: '邮箱', dataIndex: 'email', width: 200, render: (v: string) => v || '-' },
    {
      title: '角色',
      dataIndex: 'isAdmin',
      width: 100,
      render: (isAdmin: boolean) =>
        isAdmin ? <Tag color="geekblue">管理员</Tag> : <Tag>用户</Tag>,
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (v: string, r: AdminUserRow) => {
        const tag = statusTag[v] ?? { color: 'default', label: v ?? '-' };
        const node = <Tag color={tag.color}>{tag.label}</Tag>;
        return r.stopReason ? <Tooltip title={`原因：${r.stopReason}`}>{node}</Tooltip> : node;
      },
    },
    {
      title: '注册时间',
      dataIndex: 'createdAt',
      width: 170,
      render: (v: string) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-'),
    },
    {
      title: '最近登录',
      dataIndex: 'lastLogin',
      width: 170,
      render: (v: string) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-'),
    },
    {
      title: '操作',
      key: 'actions',
      width: 150,
      fixed: 'right' as const,
      render: (_: unknown, r: AdminUserRow) => {
        const isSelf = r.id === currentAdminId;
        const deleteHint = !r.isUser
          ? '该条目是客户档案，请到「客户管理」页删除'
          : isSelf
            ? '不能注销当前登录的管理员账号，否则会把自己关在后台之外'
            : '';
        return (
          <Space size="middle">
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => openStatusModal(r)}
              // 禁用自己＝下一分钟起登不进后台，恢复只能改库
              disabled={isSelf}
            >
              状态
            </Button>
            {deleteHint ? (
              <Tooltip title={deleteHint}>
                <span>
                  <Button type="link" size="small" icon={<DeleteOutlined />} disabled danger>
                    注销
                  </Button>
                </span>
              </Tooltip>
            ) : (
              <Button
                type="link"
                size="small"
                icon={<DeleteOutlined />}
                danger
                onClick={() => setDeleteTarget(r)}
              >
                注销
              </Button>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <Flex vertical gap={16}>
      <Card
        title="用户管理"
        extra={
          <Space>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="搜索姓名 / 用户名 / 手机号 / 邮箱"
              style={{ width: 280 }}
              onPressEnter={(e) => {
                setKeyword((e.target as HTMLInputElement).value);
                setPage(1);
              }}
            />
            <Select
              value={type || 'all'}
              style={{ width: 120 }}
              onChange={(v) => {
                setType(v === 'all' ? '' : v);
                setPage(1);
              }}
              options={[
                { value: 'all', label: '全部' },
                { value: 'user', label: '注册用户' },
                { value: 'customer', label: '客户' },
              ]}
            />
            <ReloadOutlined onClick={() => void refetch()} style={{ cursor: 'pointer' }} spin={isFetching} />
          </Space>
        }
      >
        <Table<AdminUserRow>
          rowKey="id"
          loading={isLoading}
          dataSource={data?.items ?? []}
          locale={{ emptyText: '暂无数据' }}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total: data?.total ?? 0,
            showTotal: (t) => `共 ${t} 条`,
            onChange: (p) => setPage(p),
          }}
          scroll={{ x: 1100 }}
          columns={columns}
        />
      </Card>

      {statusTarget && (
        <Modal
          title={`变更状态：${displayName(statusTarget)}`}
          open
          onOk={submitStatus}
          okButtonProps={{ loading: statusMutation.isPending }}
          onCancel={() => setStatusTarget(null)}
        >
          <Radio.Group
            value={statusValue}
            onChange={(e) => setStatusValue(e.target.value as UserStatus)}
            options={USER_STATUSES.map((s) => ({ label: s, value: s }))}
            optionType="button"
          />
          <p style={{ marginTop: 12, color: '#666' }}>{STATUS_HELP[statusValue]}</p>
          {statusValue === '禁用' && (
            <div style={{ marginTop: 12 }}>
              <div style={{ marginBottom: 4 }}>禁用原因（必填，会展示给用户）</div>
              <Input.TextArea
                rows={3}
                maxLength={500}
                showCount
                value={stopReason}
                onChange={(e) => setStopReason(e.target.value)}
                placeholder="例如：多次提交重复订单且拒不沟通"
              />
            </div>
          )}
          {statusTarget.stopReason && statusValue !== '禁用' && (
            <p style={{ marginTop: 12, color: '#999' }}>
              当前留存原因「{statusTarget.stopReason}」将在保存后清空。
            </p>
          )}
        </Modal>
      )}

      {deleteTarget && (
        <Modal
          title="注销账号确认"
          open
          okText="确认注销"
          okButtonProps={{ danger: true, loading: deleteMutation.isPending }}
          onOk={() => deleteMutation.mutate(deleteTarget.id)}
          onCancel={() => setDeleteTarget(null)}
        >
          <p>
            即将注销「{displayName(deleteTarget)}」。
            这会清除其登录凭据并吊销全部会话，操作不可撤销。
          </p>
          <ul style={{ paddingLeft: 18, color: '#666' }}>
            <li>本人创建、且客户不是注册用户的孤立订单会一并删除（含付款凭证图片）。</li>
            <li>与他人往来的订单、备注、审核与操作审计一律保留。</li>
            <li>
              若该手机号或邮箱仍是别人的客户档案，档案会留下来当公共客户资料，
              手机号与邮箱不会被释放，本人也无法用同一号码重新注册成全新账号。
            </li>
          </ul>
        </Modal>
      )}
    </Flex>
  );
}
